/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useIntl, FormattedMessage, defineMessages} from 'react-intl'
import {Helmet} from 'react-helmet'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

import loadable from '@loadable/component'
const PageNotFound = loadable(() => import('./../page-not-found'))

// Components
import {
    Box,
    Flex,
    SimpleGrid,
    Select,
    FormControl,
    Stack,
    useDisclosure,
    Divider,
    useMediaQuery,
    useMultiStyleConfig,
    Tabs,
    TabPanels,
    TabPanel,
    Button,
    Text,
} from '@chakra-ui/react'

// Project Components
// import Pagination from '@salesforce/retail-react-app/app/components/pagination'

import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import SelectedRefinements from './partials/selected-refinements'
import EmptySearchResults from './partials/empty-results'
import PageHeader from './partials/page-header'
import AbovePageHeader from './partials/above-page-header'

// Hooks
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {useSortUrls} from '../../hooks/use-sort-urls'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'

// Others
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Constants
import {PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '../../hooks/use-wish-list'
import {useWishlistActions} from '../../hooks/use-wishlist-actions'
import {isHydrated, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {CompareProvider} from '../../context/compare'
import CompareBanner from '../product-compare/compare-banner'
import SortByButton from './partials/sort-by-button'
import InlineRefinements from './partials/inline-refinements'
import {remCalc} from '../../theme/_utils'
import FiltersButton from './partials/filters-button'
import GridViewButton from './partials/grid-view-button'
import useCustomGetCategory from '../../custom-hooks/use-custom-categories'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import ProductFilterDrawer from './partials/product-filter-drawer'
import {removeSiteLocaleFromPath} from '@salesforce/retail-react-app/app/utils/url'
import {
    useUsid,
    useShopperContext,
    useCustomer,
    useCustomerId,
} from '@salesforce/commerce-sdk-react'
import EditorialComponentsFactory from '../../components/editorial-components/editorial-components-factory'
import PlpGridCard from '../../components/editorial-components/plp-grid-card'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import ProductManuals from '../paperless/partials/product-manuals'
import SearchCategoryTabs from './partials/search-category-tabs'
import FaqArticles from './partials/faq-articles'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {usePlpProducts} from '../../custom-hooks/use-plp-products'
import AlternateMetaTag from '../../components/metatag/alternate'
import {usePlpGtmEvent} from '../../custom-hooks/use-plp-gtm-event'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {getDamProductUrl, getDamBaseUrl} from '../../custom-hooks/use-dam-image'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getMetaTags} from '../../utils/utils'
import {MAX_CACHE_AGE_PLP, STALE_WHILE_REVALIDATE_PLP} from '../../constants'
import {getProductUrl} from '../../hooks/use-product-data'
import {BRAND_LABEL} from '../../brand'
import useManuals from '../../hooks/use-manuals'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = [
    'c_clEnabled',
    'c_enabledForCountry',
    'c_enabledForReg',
    'c_isPDPOnly',
    'c_isVisible',
    'c_skuEnabledForCountry',
    'c_skuIsPDPOnly',
    'c_visibleByAll',
    'c_visibleByCorporate',
    'c_visibleByEmployee',
]

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = (props) => {
    // Using destructuring to omit properties; we must rename `isLoading` because we use a different
    // `isLoading` later in this function.
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    const {isLoading: _unusedIsLoading, staticContext, ...rest} = props
    const styles = useMultiStyleConfig('ProductList')
    const stylesGetMoreButton = useMultiStyleConfig('GetMoreButton')
    const [isMobile] = useMediaQuery(`(max-width: ${remCalc(767.9)})`)
    const [isDesktop] = useMediaQuery(`(min-width: ${remCalc(1024)})`)
    const {isOpen, onOpen, onClose} = useDisclosure()
    const navigate = useNavigation()

    const location = useLocation()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const {addLayer} = useGoogleTagManager()

    /**************** Page State ****************/
    const [gridView, setGridView] = useState(false)
    const [displayingAllFilters, setDisplayAllFilters] = useState(false)
    const [faqArticlesHits, setFaqArticlesHits] = useState(0)
    const {customParams} = getConfig()
    const {site, locale} = useMultiSite()

    const {urlSegment} = useParams()
    const urlSearchParams = new URLSearchParams(location.search)
    let searchQuery = urlSearchParams.get('q')
    const customerId = useCustomerId()
    const isSearch = !!searchQuery
    const pathnameNoLocale = removeSiteLocaleFromPath(location.pathname)
    const categoriesData = useGetCategoriesData()
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const {buildLocalizedUrl} = useMulticountryUtils()
    const {getProductManualsInLanguage} = useManuals()

    const {usid} = useUsid()
    const {data: shopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )

    const urlSegmentValid = !!urlSegment && urlSegment !== 'null'

    const {data: categoryData} = useCustomGetCategory(
        {parameters: {urlSegment: pathnameNoLocale}},
        {enabled: !isSearch && urlSegmentValid},
    )

    const isCategoryDisabled = categoryData?.c_isDisabled === 'true'

    const {data: customer} = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer},
    )

    useEffect(() => {
        const isCorporate = shopperContext?.customQualifiers?.isCorporate === 'true'
        const isEmployee = customer?.c_isEmployee

        if (isCorporate || isEmployee) {
            const searchParamsCopy = {...searchParams}
            delete searchParamsCopy.offset
            delete searchParamsCopy.page
            delete searchParamsCopy.limit
            delete searchParamsCopy.sort

            let doRedirect = false

            if (isCorporate && searchParams?.refine?.corporate !== true) {
                doRedirect = true
                searchParamsCopy.refine['corporate'] = 'true'
            }
            if (isEmployee && searchParams?.refine?.employee !== true) {
                doRedirect = true
                searchParamsCopy.refine['employee'] = 'true'
            }

            if (doRedirect)
                navigate(
                    `${pathnameNoLocale}?${stringifySearchParams(searchParamsCopy)}`,
                    'replace',
                )
        }
    }, [shopperContext, customer])

    const {
        productSearchResult,
        productsCount,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError: isPlpFetchProductsError,
        previousPage,
        isLoading,
        prefetchNextPage,
        prefetchPreviousPage,
        isFirstPageFetched,
    } = usePlpProducts(
        {
            isSearch,
            urlSegment: pathnameNoLocale,
            searchParams,
            category: categoryData,
            expand: ['represented_products', 'page_meta_tags'],
        },
        {
            enabled: urlSegmentValid,
        },
    )

    const allHits = productSearchResult?.hits || []

    const pageIsPlpPlus = categoryData?.c_isPlpPlus === 'true'
    const plpPlusData = categoryData?.plpPlusData

    const filterRefinements = (refinements) =>
        refinements.filter(({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)) || []

    /**************** Mutation Actions ****************/
    const category = useRef()
    const filtersRef = useRef(filterRefinements(productSearchResult?.refinements || []))

    const setCurrentCategory = (currentCategory) => {
        category.current = currentCategory
    }

    useEffect(() => {
        if (productSearchResult?.refinements) {
            filtersRef.current = filterRefinements(productSearchResult.refinements)
        }
    }, [productSearchResult?.refinements])

    setCurrentCategory(categoryData)

    // Apply disallow list to refinements.
    if (productSearchResult?.refinements && Array.isArray(productSearchResult.refinements)) {
        productSearchResult.refinements = filterRefinements(productSearchResult.refinements)
    }

    if (productSearchResult?.selectedRefinements) {
        productSearchResult.selectedRefinements = Object.fromEntries(
            Object.entries(productSearchResult.selectedRefinements).filter(
                ([key]) => !REFINEMENT_DISALLOW_LIST.includes(key),
            ),
        )
    }

    /**************** Response Handling ****************/
    if (res && allHits.length > 0) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_PLP}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_PLP}`,
        )
    }

    /**************** Render Variables ****************/
    //const basePath = `${location.pathname}${location.search}`
    const showNoResults =
        !isLoading && productSearchResult && productSearchResult?.hits?.length === 0

    const {sortingOptions} = productSearchResult || {}
    const selectedSortingOptionLabel =
        sortingOptions?.find(
            (option) => option.id === productSearchResult?.selectedSortingOption,
        ) ?? sortingOptions?.[0]

    // Get urls to be used for pagination, page size changes, and sorting.

    const sortUrls = useSortUrls({options: sortingOptions})

    /**************** Action Handlers ****************/
    const {data: wishlist} = useWishList()
    const {addItemToWishlist, removeItemFromWishlist, isProductInWishlist} =
        useWishlistActions(wishlist)

    // Toggles filter on and off
    const toggleFilter = useCallback(
        async (value, attributeId, keyLabel, selected, allowMultiple = true) => {
            const searchParamsCopy = {...searchParams}

            // Remove the `offset` search param if present.
            delete searchParamsCopy.offset
            delete searchParamsCopy.page
            delete searchParamsCopy.limit

            // If we aren't allowing for multiple selections, simply clear any value set for the
            // attribute, and apply a new one if required.
            if (!allowMultiple) {
                const previousValue = searchParamsCopy.refine[attributeId]
                delete searchParamsCopy.refine[attributeId]

                // Note the loose comparison, for "string != number" checks.
                // leave the add Layer for the Price filter.
                // we need to send the label and not the value cause of the format to send
                if (!selected && value.value != previousValue) {
                    searchParamsCopy.refine[attributeId] = value.value
                    await addLayer(
                        basic_gtm_event.bind(this, {
                            event: 'filter',
                            eventCategory: 'engagement',
                            eventAction: 'filter_apply',
                            eventLabel: keyLabel,
                            eventDetail: value.label,
                        }),
                    )
                }
            } else {
                // Get the attibute value as an array.
                let attributeValue = searchParamsCopy.refine[attributeId] || []

                // Ensure that the value is still converted into an array if it's a `string` or `number`.
                if (typeof attributeValue === 'string') {
                    attributeValue = attributeValue.split('|')
                } else if (typeof attributeValue === 'number') {
                    attributeValue = [attributeValue]
                }

                // Either set the value, or filter the value out.
                if (!selected) {
                    attributeValue.push(value.value)
                    await addLayer(
                        basic_gtm_event.bind(this, {
                            event: 'filter',
                            eventCategory: 'engagement',
                            eventAction: 'filter_apply',
                            eventLabel: keyLabel,
                            eventDetail: value.value,
                        }),
                    )
                } else {
                    // Note the loose comparison, for "string != number" checks.
                    attributeValue = attributeValue?.filter((v) => v != value.value)
                }

                // Update the attribute value in the new search params.
                searchParamsCopy.refine[attributeId] = attributeValue

                // If the update value is an empty array, remove the current attribute key.
                if (searchParamsCopy.refine[attributeId].length === 0) {
                    delete searchParamsCopy.refine[attributeId]
                }
            }

            if (isSearch) navigate(`/search?${stringifySearchParams(searchParamsCopy)}`)
            else navigate(`${pathnameNoLocale}?${stringifySearchParams(searchParamsCopy)}`)
        },
    )

    // Clears all filters
    const resetFilters = () => {
        const newSearchParams = {
            ...searchParams,
            refine: [],
        }
        delete newSearchParams.page
        const newPath = isSearch
            ? `/search?${stringifySearchParams(newSearchParams)}`
            : `${pathnameNoLocale}?${stringifySearchParams(newSearchParams)}`

        navigate(newPath)
        onClose()
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (productSearchResult) {
            if (isSearch) {
                try {
                    // einstein.sendViewSearch(searchQuery, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewSearch error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, searchQuery},
                    })
                }
                activeData.sendViewSearch(searchParams, productSearchResult)
            } else {
                try {
                    // einstein.sendViewCategory(category, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewCategory error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, category},
                    })
                }
                activeData.sendViewCategory(searchParams, category, productSearchResult)
            }
        }
    }, [productSearchResult])

    function ProductTileLoop(productSearchItems) {
        return productSearchItems?.map((productSearchItem, i) => {
            const productId = productSearchItem?.productId
            const isInWishlist = isProductInWishlist(productId)

            const handleProductClick = () => {
                if (searchQuery) {
                    // einstein.sendClickSearch(searchQuery, productSearchItem)
                } else if (category) {
                    // einstein.sendClickCategory(category, productSearchItem)
                }
            }

            return (
                <ProductTile
                    data-testid={`sf-product-tile-${productSearchItem.productId}`}
                    key={productSearchItem.productId}
                    product={productSearchItem}
                    productIndex={i}
                    enableFavourite
                    isFavourite={isInWishlist}
                    selectableAttributeId={PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID}
                    gridView={gridView}
                    onClick={handleProductClick}
                    onFavouriteToggle={(toBeFavourite) => {
                        const action = toBeFavourite ? addItemToWishlist : removeItemFromWishlist
                        return action(productSearchItem)
                    }}
                    dynamicImageProps={{
                        widths: ['50vw', '50vw', '20vw', '20vw', '25vw'],
                    }}
                    categoryName={categoryData?.name}
                    isFromProductList={true}
                    isFetchPriority={i === 0 && !pageIsPlpPlus}
                />
            )
        })
    }

    function ProductTileLoopEditorial(productSearchItems) {
        const gridEditorialComponent = plpPlusData?.grid_editorial_component[0]
        if (!pageIsPlpPlus || !gridEditorialComponent || !isFirstPageFetched)
            return ProductTileLoop(productSearchItems)

        const firstPart = productSearchItems.slice(0, 4)
        const secondPart = productSearchItems.slice(4)
        return (
            <>
                {ProductTileLoop(firstPart)}
                <PlpGridCard
                    colSpan={!gridView && isMobile ? 1 : 2}
                    title={gridEditorialComponent?.grid_component_title}
                    images={gridEditorialComponent.images}
                    redirects={gridEditorialComponent.redirects}
                    categoriesData={categoriesData}
                />
                {ProductTileLoop(secondPart)}
            </>
        )
    }

    const tabButtonLabels = defineMessages({
        productResults: {
            id: 'product_list.product_results',
            defaultMessage: 'Products ({total})',
        },
        faq: {
            id: 'product_list.frequently_asked_questions',
            defaultMessage: 'Frequently Asked Questions ({total})',
        },
        manuals: {
            id: 'product_list.instruction_manuals',
            defaultMessage: 'Instruction Manuals ({total})',
        },
    })
    const [tabIndex, setTabIndex] = useState(Object.keys(tabButtonLabels).indexOf('productResults'))
    const handleTabChange = (i) => {
        if (Object.keys(tabButtonLabels)[i] === 'productResults')
            urlSearchParams.delete('searchType')
        else urlSearchParams.set('searchType', Object.keys(tabButtonLabels)[i])
        setTabIndex(i)
        navigate(`${location.pathname}?${urlSearchParams.toString()}`)
    }

    useEffect(() => {
        if (urlSearchParams.get('searchType'))
            setTabIndex(Object.keys(tabButtonLabels).indexOf(urlSearchParams.get('searchType')))
    }, [tabIndex])

    //?GTM Hook for PLP
    usePlpGtmEvent(productSearchResult, isSearch, categoryData, pageIsPlpPlus, isLoading)

    const currentPathname = categoryData?.c_slugUrl
        ? buildLocalizedUrl(categoryData?.c_slugUrl)
        : location.pathname
    const currentPath = 'https://' + customParams.currentHost + currentPathname

    const categories = category?.current?.parentCategoryTree || category?.parentCategoryTree || []

    const categoryName =
        categories?.length > 0 ? categories[categories.length - 1]?.name : 'Product List Page'

    const metaDescription =
        productSearchResult?.pageMetaTags?.find((tag) => tag.id === 'description')?.value || ''

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: categoryName,
        description: metaDescription,
        url: currentPath,
    }

    const itemListElement = allHits.map((product, index) => {
        return {
            '@type': 'ListItem',
            position: index + 1,
            url: 'https://' + customParams.currentHost + getProductUrl(product),
        }
    })

    const itemListSchemaMarkup = {
        '@context': 'http://schema.org',
        '@type': 'ItemList',
        itemListElement,
    }

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: categories.map((cat, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@id': 'https://' + customParams.currentHost + (cat.c_slugUrl || ''),
                name: cat.name,
            },
        })),
    }

    /**************** Error Handling ****************/
    if (!isLoading && (categoryData?.error || isPlpFetchProductsError)) {
        if (res) res.status(404)
        return <PageNotFound />
    }
    if (isCategoryDisabled) {
        return <PageNotFound />
    }

    return (
        <Box
            className="sf-product-list-page"
            data-testid="sf-product-list-page"
            data-gtm={isSearch ? 'internal-search-result-page' : 'product-list-page'}
            {...rest}
        >
            <Helmet>
                {getMetaTags(productSearchResult?.pageMetaTags)}

                <link rel="preconnect" href={getDamProductUrl()} />
                {pageIsPlpPlus && <link rel="preconnect" href={getDamBaseUrl()} />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(itemListSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
                <link rel="canonical" href={currentPath} />
            </Helmet>
            {categoryData?.c_hreflangs && <AlternateMetaTag links={categoryData.c_hreflangs} />}
            <Box layerStyle="page">
                <AbovePageHeader />
                <PageHeader
                    searchQuery={searchQuery}
                    category={category.current}
                    productSearchResult={productSearchResult}
                    isLoading={isLoading}
                    editorialComponents={
                        pageIsPlpPlus || plpPlusData?.top_editorial_components
                            ? plpPlusData?.top_editorial_components
                            : undefined
                    }
                />
            </Box>
            {!showNoResults ? (
                <Tabs isLazy onChange={handleTabChange} index={tabIndex}>
                    <Box layerStyle="page">
                        <SearchCategoryTabs
                            productSearchResult={productSearchResult}
                            tabButtonLabels={tabButtonLabels}
                            faqArticlesHits={faqArticlesHits}
                            isSearch={isSearch}
                        />
                    </Box>
                    <TabPanels>
                        <TabPanel px={remCalc(0)}>
                            <Box layerStyle="page">
                                <Stack
                                    {...styles.refinementsStack}
                                    marginBottom={isSearch && 'medium.lg'}
                                >
                                    <Flex align="center" display={{base: 'none', lg: 'block'}}>
                                        <InlineRefinements
                                            filtersRef={filtersRef}
                                            toggleFilter={toggleFilter}
                                            selectedFilters={searchParams.refine}
                                            setDisplayAllFilters={setDisplayAllFilters}
                                            itemsAfter={[
                                                <SortByButton
                                                    key="sortby"
                                                    selectedSortingOptionLabel={
                                                        selectedSortingOptionLabel
                                                    }
                                                    sortUrls={sortUrls}
                                                    productSearchResult={productSearchResult}
                                                />,
                                            ]}
                                            excludedFilters={['cgid']}
                                        />
                                    </Flex>
                                    <Flex align="right">
                                        {(!isDesktop || !displayingAllFilters) && (
                                            <FiltersButton buttonClick={onOpen} />
                                        )}
                                    </Flex>
                                    {isMobile && (
                                        <Flex>
                                            <GridViewButton
                                                gridView={gridView}
                                                setGridView={setGridView}
                                            />
                                        </Flex>
                                    )}
                                </Stack>
                                {!isSearch && <Divider {...styles.refinementsDivider} />}
                                <Box {...styles.selectedRefinementsContainer}>
                                    <SelectedRefinements
                                        filters={productSearchResult?.refinements}
                                        toggleFilter={toggleFilter}
                                        handleReset={resetFilters}
                                        selectedFilterValues={
                                            productSearchResult?.selectedRefinements
                                        }
                                    />
                                </Box>
                                {(hasPreviousPage || isFetchingPreviousPage) && !isLoading && (
                                    <Flex justify="center">
                                        <ViewPreviousButton
                                            buildLocalizedUrl={buildLocalizedUrl}
                                            previousPage={previousPage}
                                            fetchPreviousPage={fetchPreviousPage}
                                            productSearchResult={productSearchResult}
                                            isFetchingPreviousPage={isFetchingPreviousPage}
                                            prefetchPreviousPage={prefetchPreviousPage}
                                            customParams={customParams}
                                            location={location}
                                        />
                                    </Flex>
                                )}
                                <SimpleGrid
                                    columns={gridView ? [2, 2, 2, 4] : [1, 1, 2, 4]}
                                    {...styles.productsGrid}
                                >
                                    {isHydrated() && isLoading
                                        ? new Array(searchParams.limit)
                                              .fill(0)
                                              .map((value, index) => (
                                                  <ProductTileSkeleton key={index} />
                                              ))
                                        : ProductTileLoopEditorial(allHits)}
                                </SimpleGrid>
                                <Flex {...stylesGetMoreButton.container}>
                                    <Text {...stylesGetMoreButton.label}>
                                        <FormattedMessage
                                            defaultMessage={
                                                'You are viewing {alreadyLoadedItems} of {totalItems} products'
                                            }
                                            values={{
                                                alreadyLoadedItems:
                                                    BRAND_LABEL === 'Kenwood' ||
                                                    BRAND_LABEL === 'Braun' ? (
                                                        <b>{productsCount}</b>
                                                    ) : (
                                                        productsCount
                                                    ),
                                                totalItems:
                                                    BRAND_LABEL === 'Kenwood' ||
                                                    BRAND_LABEL === 'Braun' ? (
                                                        <b>{productSearchResult?.total ?? 0}</b>
                                                    ) : (
                                                        productSearchResult?.total ?? 0
                                                    ),
                                            }}
                                            id="product_list.getmore.itemcount"
                                        />
                                    </Text>

                                    {(hasNextPage || isFetchingNextPage) && !isLoading && (
                                        <ViewMoreButton
                                            fetchNextPage={fetchNextPage}
                                            productSearchResult={productSearchResult}
                                            isFetchingNextPage={isFetchingNextPage}
                                            stylesGetMoreButton={stylesGetMoreButton}
                                            prefetchNextPage={prefetchNextPage}
                                            buildLocalizedUrl={buildLocalizedUrl}
                                            customParams={customParams}
                                            location={location}
                                        />
                                    )}
                                </Flex>
                            </Box>
                            {(pageIsPlpPlus || plpPlusData?.bottom_editorial_components) &&
                                plpPlusData?.bottom_editorial_components.map((component) => (
                                    <Box
                                        key={component.uid}
                                        {...styles.editorialComponentsContainer}
                                    >
                                        <EditorialComponentsFactory
                                            componentType={component?._content_type_uid}
                                            {...component}
                                            titleAs={
                                                component?._content_type_uid === 'text_group' ||
                                                component?._content_type_uid === 'sidecard'
                                                    ? 'h2'
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                ))}
                        </TabPanel>
                        <TabPanel px={remCalc(0)}>
                            <Box layerStyle="page" pt={{base: remCalc(8), lg: remCalc(24)}}>
                                {isSearch && (
                                    <FaqArticles
                                        searchQuery={searchQuery}
                                        setFaqArticlesHits={setFaqArticlesHits}
                                    />
                                )}
                            </Box>
                        </TabPanel>
                        <TabPanel {...styles.manualsTabPanel}>
                            <Box layerStyle="page">
                                {isSearch &&
                                    productSearchResult?.hits?.map((product) => {
                                        const productManuals = getProductManualsInLanguage(
                                            product?.representedProduct,
                                            locale.id,
                                        )

                                        return (
                                            <ProductManuals
                                                key={product.productId}
                                                product={product?.representedProduct}
                                                productManuals={productManuals?.manuals || []}
                                                variant="plpManuals"
                                            />
                                        )
                                    })}
                            </Box>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            ) : (
                <EmptySearchResults searchQuery={searchQuery} category={category.current} />
            )}
            <ProductFilterDrawer
                searchParams={searchParams}
                productSearchResult={productSearchResult}
                selectedSortingOptionLabel={selectedSortingOptionLabel}
                sortUrls={sortUrls}
                category={category.current}
                isOpen={isOpen}
                onClose={onClose}
                filtersLoading={isLoading}
                toggleFilter={toggleFilter}
                resetFilters={resetFilters}
            />
            <CompareBanner />
            <Box id="plp_product_recommentations" />
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.propTypes = {
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object,
}

const ProductListContainer = (props) => {
    return (
        <CompareProvider>
            <ProductList {...props} />
        </CompareProvider>
    )
}

export default ProductListContainer

const Sort = ({sortUrls, productSearchResult, basePath, ...otherProps}) => {
    const intl = useIntl()
    const history = useHistory()

    return (
        <FormControl
            aria-label={intl.formatMessage({
                id: 'product_list.drawer.title.sort_by',
                defaultMessage: 'Sort By',
            })}
            data-testid="sf-product-list-sort"
            id="page_sort"
            width="auto"
            {...otherProps}
        >
            <Select
                value={basePath.replace(/(offset)=(\d+)/i, '$1=0')}
                onChange={({target}) => {
                    history.push(target.value)
                }}
                height={11}
                width="240px"
            >
                {sortUrls.map((href, index) => (
                    <option key={href} value={href}>
                        {intl.formatMessage(
                            {
                                id: 'product_list.select.sort_by',
                                defaultMessage: 'Sort By: {sortOption}',
                            },
                            {
                                sortOption: productSearchResult?.sortingOptions[index]?.label,
                            },
                        )}
                    </option>
                ))}
            </Select>
        </FormControl>
    )
}

Sort.propTypes = {
    sortUrls: PropTypes.array,
    productSearchResult: PropTypes.object,
    basePath: PropTypes.string,
}

const ViewMoreButton = ({
    fetchNextPage,
    productSearchResult,
    isFetchingNextPage,
    stylesGetMoreButton,
    prefetchNextPage,
    buildLocalizedUrl,
    customParams,
    location,
}) => {
    useEffect(() => {
        prefetchNextPage()
    }, [productSearchResult.page])

    return (
        <Button
            as="a"
            variant="secondary"
            onClick={(e) => {
                e.preventDefault()
                fetchNextPage()
            }}
            href={`https://${customParams.currentHost}${buildLocalizedUrl(
                location.pathname,
            )}?page=${productSearchResult.page + 1}`}
            isLoading={isFetchingNextPage}
            {...stylesGetMoreButton?.buttonAdditional}
            type="button"
        >
            <FormattedMessage defaultMessage="view more" id="product_list.getmore.loadmorebutton" />
        </Button>
    )
}

ViewMoreButton.propTypes = {
    fetchNextPage: PropTypes.func.isRequired,
    productSearchResult: PropTypes.object.isRequired,
    isFetchingNextPage: PropTypes.bool.isRequired,
    stylesGetMoreButton: PropTypes.object,
    prefetchNextPage: PropTypes.func.isRequired,
    buildLocalizedUrl: PropTypes.func.isRequired,
    customParams: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
}

const ViewPreviousButton = ({
    previousPage,
    isFetchingPreviousPage,
    stylesGetMoreButton,
    fetchPreviousPage,
    buildLocalizedUrl,
    prefetchPreviousPage,
    customParams,
    location,
}) => {
    useEffect(() => {
        prefetchPreviousPage()
    }, [previousPage])

    return (
        <Button
            as="a"
            variant="secondary"
            onClick={(e) => {
                e.preventDefault()
                fetchPreviousPage()
            }}
            href={`https://${customParams.currentHost}${buildLocalizedUrl(location.pathname)}${
                previousPage > 1 ? `?page=${previousPage}` : ''
            }`}
            isLoading={isFetchingPreviousPage}
            {...stylesGetMoreButton?.buttonAdditional}
            margin="auto"
            type="button"
        >
            <FormattedMessage
                defaultMessage={'view previous'}
                // values={{addMore: addMoreItems}}
                id="product_list.getprevious.loadmorebutton"
            />
        </Button>
    )
}

ViewPreviousButton.propTypes = {
    previousPage: PropTypes.number,
    fetchPreviousPage: PropTypes.func.isRequired,
    productSearchResult: PropTypes.object.isRequired,
    isFetchingPreviousPage: PropTypes.bool.isRequired,
    stylesGetMoreButton: PropTypes.object,
    buildLocalizedUrl: PropTypes.func.isRequired,
    prefetchPreviousPage: PropTypes.func.isRequired,
    customParams: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
}
